<script setup lang="ts">
import type { ContentArticleSequence } from '~/models/Content/ContentArticleSequence'
import type { ArticleContent } from '~/models/Content/ArticleContent'
import { computed, ref } from 'vue'
import { vIntersectionObserver } from '@vueuse/components'
import { useQuery } from '@tanstack/vue-query'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import ArticleSequenceImage from '~/components/article/ArticleSequenceImage.vue'

const { item } = defineProps<{ item: ContentArticleSequence }>()

const { findContents } = useContentApi()

const activeIndex = ref(0)

const { data: sequences, isLoading } = useQuery({
  queryKey: ['article_sequence', item.locationId],
  queryFn: () => findContents<ArticleContent>({
    contentTypeCriterion: [ContentType.ArticleContent],
    contentIdCriterion: item.sequences.destinationContentIds,
    mainLocationCriterion: true,
    subtreeCriterion: [Subtree.Content, Subtree.Media],
  }, item.sequences.destinationContentIds.length),
  enabled: item.sequences.destinationContentIds.length > 0,
  select: sequences => sequences
    .sort((a, b) => item.sequences.destinationContentIds.indexOf(a.contentId) - item.sequences.destinationContentIds.indexOf(b.contentId)),
  staleTime: Infinity,
  retry: 1,
})

const firstSequence = computed(() => sequences.value?.[0])

function onIntersect(index: number) {
  return ([{ isIntersecting }]: IntersectionObserverEntry[]) => {
    if (isIntersecting) {
      activeIndex.value = index
    }
  }
}
</script>

<template>
  <section
    v-if="!isLoading && sequences?.length"
    class="col-[media] grid grid-cols-7 gap-x-(--col-gap)"
    style="font-size: inherit;"
  >
    <div class="relative col-span-3">
      <div v-if="item.staticText" class="pointer-events-none absolute inset-0">
        <div class="sticky top-0 grid h-dvh items-center">
          <div class="pointer-events-auto space-y-4">
            <h2
              v-if="!firstSequence?.hideTitle"
              class="text-[1.8em] font-bold"
              v-text="firstSequence?.title"
            />
            <div class="au-prose">
              <RichTextRenderer v-if="firstSequence?.body" :text="firstSequence?.body" />
            </div>
          </div>
        </div>
      </div>

      <div
        v-for="(sequence, index) in sequences"
        :key="sequence.contentId"
        v-intersection-observer="[onIntersect(index), { threshold: 0.5 }]"
        class="flex min-h-dvh snap-start snap-always items-center"
      >
        <div
          v-if="!item.staticText"
          class="space-y-4 transition-[opacity,transform] duration-700 ease-out will-change-[opacity,transform] motion-reduce:transition-none"
          :class="index === activeIndex ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
        >
          <h2
            v-if="!sequence.hideTitle"
            class="text-[1.8em] font-bold"
            v-text="sequence.title"
          />
          <div class="au-prose">
            <RichTextRenderer v-if="sequence.body" :text="sequence.body" />
          </div>
        </div>

        <div
          v-else
          class="h-full w-full"
          aria-hidden="true"
        />
      </div>
    </div>

    <div class="col-span-4">
      <div class="sticky top-0 grid h-dvh items-center">
        <div
          v-for="(sequence, index) in sequences"
          :key="sequence.contentId"
          class="will-change-opacity relative col-start-1 row-start-1 transition-opacity duration-700 ease-out motion-reduce:transition-none"
          :class="index <= activeIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'"
        >
          <ArticleSequenceImage :sequence :active-index="index === activeIndex" />
        </div>
      </div>
    </div>
  </section>
</template>
